/**
 * AudioPlayerContext.tsx — estado global del reproductor ambiental de audio.
 *
 * Reemplaza al `EventAudioPlayer` sticky que vivía en /eventos/:slug. Ahora:
 *  · Cualquier página del sitio puede "prestar" una URL al player global vía
 *    `setAudio(url, label)` desde un `useEffect`, y limpiarla en cleanup.
 *  · El `<audio>` real vive UNA sola vez en el layout (`GlobalAudioPlayer`),
 *    así que navegar entre rutas no reinicia el track ni crea audios fantasma.
 *  · Un pequeño FAB en la esquina inferior izquierda permite mute/unmute; sólo
 *    aparece cuando hay una URL activa.
 *  · La preferencia `isMuted` se persiste en localStorage vía el helper
 *    `usePersistedMute` ya existente (`ceciluz_events_audio_muted`), así que
 *    si el usuario silencia en Evento A, sigue silenciado en Evento B.
 *
 * Diseño: Context de React (no Zustand — el proyecto ya usa Context para auth,
 * toasts, etc.). Suficiente porque el consumidor real es 1: el `GlobalAudioPlayer`.
 * Los productores (páginas de detalle) sólo llaman acciones al montar/desmontar.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { usePersistedMute } from '../events/useAudioPersistence';

// ---------------------------------------------------------------------------
// Tipos
// ---------------------------------------------------------------------------

/** Snapshot del state expuesto a los consumers. */
export interface AudioPlayerState {
  /** URL absoluta del track actualmente cargado (null = no hay track). */
  audioUrl: string | null;
  /** Etiqueta editorial del track (ej. "Cuencos tibetanos"), sólo informativa. */
  label: string | null;
  /** True si el `<audio>` está reproduciendo (sincronizado por el player). */
  isPlaying: boolean;
  /** True si el usuario tiene el sonido silenciado. Persistido en localStorage. */
  isMuted: boolean;
  /**
   * True cuando el browser rechazó el autoplay incluso muteado y esperamos
   * el primer gesto. Lo prende el `GlobalAudioPlayer`, lo consulta el FAB
   * para señalar visualmente el estado (opcional).
   */
  needsUserGesture: boolean;
}

/** Acciones que los componentes pueden disparar. */
export interface AudioPlayerActions {
  /** Registra un audio para reproducir globalmente. Idempotente por URL. */
  setAudio: (url: string, label?: string | null) => void;
  /** Limpia la URL — el player se pausa y el FAB desaparece. */
  clearAudio: () => void;
  /** Alterna mute/unmute. Persiste en localStorage. */
  toggleMute: () => void;
  /** Set explícito del flag mute (usado internamente por el player). */
  setMuted: (muted: boolean) => void;
  /** Sync interno desde el `<audio>` real (usado por GlobalAudioPlayer). */
  markPlaying: (playing: boolean) => void;
  /** Sync interno cuando el browser bloquea autoplay. */
  markNeedsUserGesture: (needs: boolean) => void;
}

/** Contexto combinado state + actions. Un solo consumer hook los recibe todos. */
type AudioPlayerContextValue = AudioPlayerState & AudioPlayerActions;

const AudioPlayerContext = createContext<AudioPlayerContextValue | undefined>(undefined);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface AudioPlayerProviderProps {
  children: ReactNode;
}

export function AudioPlayerProvider({ children }: AudioPlayerProviderProps) {
  // URL + label: state en memoria. NO persistimos entre navegaciones/reload
  // — cada página de detalle vuelve a inyectar su URL al montar.
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [label, setLabel] = useState<string | null>(null);

  // Playback state — lo sincroniza `GlobalAudioPlayer` desde los eventos del
  // <audio> real. No es fuente de verdad para persistir, sólo para UI.
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [needsUserGesture, setNeedsUserGesture] = useState<boolean>(false);

  // Mute: persistido en localStorage vía el hook existente
  // (`ceciluz_events_audio_muted`), default `true` para respetar autoplay
  // policies y a usuarios en contextos públicos.
  const [isMuted, setMutedInternal] = usePersistedMute(true);

  const setAudio = useCallback<AudioPlayerActions['setAudio']>((url, nextLabel) => {
    setAudioUrl(url);
    setLabel(nextLabel ?? null);
  }, []);

  const clearAudio = useCallback<AudioPlayerActions['clearAudio']>(() => {
    setAudioUrl(null);
    setLabel(null);
    setIsPlaying(false);
    setNeedsUserGesture(false);
  }, []);

  const toggleMute = useCallback<AudioPlayerActions['toggleMute']>(() => {
    setMutedInternal(!isMuted);
    // Si el usuario destacó el ícono para escuchar, borramos el flag de
    // gesture: el próximo intento de play sí puede arrancar con sonido.
    if (isMuted) setNeedsUserGesture(false);
  }, [isMuted, setMutedInternal]);

  const setMuted = useCallback<AudioPlayerActions['setMuted']>(
    (next) => setMutedInternal(next),
    [setMutedInternal],
  );

  const markPlaying = useCallback<AudioPlayerActions['markPlaying']>(
    (playing) => setIsPlaying(playing),
    [],
  );

  const markNeedsUserGesture = useCallback<AudioPlayerActions['markNeedsUserGesture']>(
    (needs) => setNeedsUserGesture(needs),
    [],
  );

  const value = useMemo<AudioPlayerContextValue>(
    () => ({
      audioUrl,
      label,
      isPlaying,
      isMuted,
      needsUserGesture,
      setAudio,
      clearAudio,
      toggleMute,
      setMuted,
      markPlaying,
      markNeedsUserGesture,
    }),
    [
      audioUrl,
      label,
      isPlaying,
      isMuted,
      needsUserGesture,
      setAudio,
      clearAudio,
      toggleMute,
      setMuted,
      markPlaying,
      markNeedsUserGesture,
    ],
  );

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook consumer
// ---------------------------------------------------------------------------

/**
 * Hook para consumir el state + actions del player global. Lanza si se usa
 * fuera del provider — evita fallos silenciosos por olvidar montar el
 * `<AudioPlayerProvider>` en el layout.
 */
export function useAudioPlayer(): AudioPlayerContextValue {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) {
    throw new Error(
      'useAudioPlayer debe usarse dentro de <AudioPlayerProvider>. ' +
        'Verifica que MainLayout lo esté montando en el árbol.',
    );
  }
  return ctx;
}
